import { useEffect, useState } from 'react'
import { BarChart3 } from 'lucide-react'
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { useCommandDashboard } from '@/contexts/CommandDashboardContext'
import { fetchCommandAnalytics } from '@/lib/api/command'
import { parseDashboardMoney } from '@/lib/dashboard/commandMetrics'
import { cn } from '@/lib/utils'

const TABS = ['today', 'week', 'month']

export function BusinessAnalytics() {
  const { data } = useCommandDashboard()
  const [period, setPeriod] = useState('today')
  const [periodSeries, setPeriodSeries] = useState(null)
  const forecast = data?.revenue_forecast ?? {}
  const analytics = data?.analytics ?? {}

  const current = parseDashboardMoney(forecast.current)
  const projected = parseDashboardMoney(forecast.forecast)
  const target = parseDashboardMoney(forecast.target)
  const likelihood = Number(forecast.likelihood_pct ?? 0)

  useEffect(() => {
    if (period === 'today' && Array.isArray(analytics.today) && analytics.today.length > 0) {
      setPeriodSeries(analytics.today)
      return
    }

    let cancelled = false
    fetchCommandAnalytics(period)
      .then((payload) => {
        if (!cancelled && Array.isArray(payload.series)) {
          setPeriodSeries(payload.series)
        }
      })
      .catch(() => {
        if (!cancelled) setPeriodSeries(null)
      })

    return () => {
      cancelled = true
    }
  }, [period, analytics.today])

  const chartData = periodSeries && periodSeries.length > 0 ? periodSeries : []
  const progress = target > 0 ? Math.round((current / target) * 100) : 0
  const forecastPct = target > 0 ? Math.round((projected / target) * 100) : 0
  const vsPace = projected - current
  const useBarChart = period !== 'today'

  return (
    <section className="card-revenue overflow-hidden p-5 lg:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <BarChart3 className="h-5 w-5" strokeWidth={2.25} />
          </div>
          <div>
            <h2 className="text-base font-bold tracking-tight text-foreground">Revenue performance</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Actual vs projected · owner view
            </p>
          </div>
        </div>
        <div className="flex rounded-xl bg-muted/60 p-0.5 shadow-inner">
          {TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setPeriod(tab)}
              className={cn(
                'rounded-lg px-3.5 py-1.5 text-[11px] font-semibold capitalize transition-all',
                period === tab
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
        {[
          { label: 'Current', value: current, tone: 'text-foreground' },
          { label: 'Forecast', value: projected, tone: 'text-primary' },
          { label: 'Target', value: target, tone: 'text-[hsl(31_45%_42%)]' },
          {
            label: 'Likelihood',
            value: likelihood,
            tone: 'text-emerald-600',
            suffix: '%',
            money: false,
          },
        ].map((stat) => (
          <div
            key={stat.label}
            className="rounded-2xl bg-white/60 px-3 py-3 shadow-sm backdrop-blur-sm"
            style={{ border: '1px solid hsl(262 20% 50% / 0.06)' }}
          >
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              {stat.label}
            </p>
            <p className={cn('mt-1 text-lg font-bold tabular-nums sm:text-xl', stat.tone)}>
              {stat.money === false
                ? `${stat.value}${stat.suffix ?? ''}`
                : `AED ${stat.value.toLocaleString()}`}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap gap-4 text-[10px] font-medium">
        <span className="flex items-center gap-1.5">
          <span className="h-0.5 w-5 rounded-full bg-primary" />
          Actual revenue
        </span>
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <span className="h-0.5 w-5 rounded-full border-t-2 border-dashed border-secondary" />
          Projected
        </span>
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <span className="h-0.5 w-5 rounded-full bg-gold" />
          Target · {progress}%
        </span>
        <span className="text-muted-foreground">
          {vsPace >= 0 ? '+' : ''}AED {Math.abs(vsPace).toLocaleString()} vs pace
        </span>
      </div>

      <div className="mt-4 h-52 w-full min-w-0 sm:h-60 lg:h-72">
        {chartData.length === 0 ? (
          <div className="flex h-full items-center justify-center rounded-2xl bg-muted/20 text-sm text-muted-foreground">
            No revenue data for this period yet.
          </div>
        ) : useBarChart ? (
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={208}>
            <BarChart data={chartData} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(262 10% 93%)" vertical={false} />
              <XAxis
                dataKey="t"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: 'hsl(262 8% 46%)' }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: 'hsl(262 8% 46%)' }}
                tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                width={40}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 12,
                  fontSize: 11,
                  border: 'none',
                  boxShadow: '0 4px 24px rgba(124, 58, 237, 0.12)',
                }}
                formatter={(v) => (v != null ? [`AED ${Number(v).toLocaleString()}`, ''] : null)}
              />
              <Bar dataKey="v" fill="#7C3AED" radius={[6, 6, 0, 0]} maxBarSize={36} />
              <Bar dataKey="projected" fill="#C084FC" radius={[6, 6, 0, 0]} maxBarSize={36} opacity={0.65} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={208}>
            <ComposedChart data={chartData}>
              <defs>
                <linearGradient id="revGradPremium" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#7C3AED" stopOpacity={0.4} />
                  <stop offset="50%" stopColor="#C084FC" stopOpacity={0.15} />
                  <stop offset="100%" stopColor="#E9D5FF" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="projGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C084FC" stopOpacity={0.1} />
                  <stop offset="100%" stopColor="#C084FC" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(262 10% 93%)" vertical={false} />
              <XAxis
                dataKey="t"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: 'hsl(262 8% 46%)' }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: 'hsl(262 8% 46%)' }}
                tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                width={40}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 12,
                  fontSize: 11,
                  border: 'none',
                  boxShadow: '0 4px 24px rgba(124, 58, 237, 0.12)',
                }}
                formatter={(v, name) => {
                  const labels = { v: 'Revenue', projected: 'Projected', pace: 'Pace' }
                  return v != null ? [`AED ${Number(v).toLocaleString()}`, labels[name] ?? name] : null
                }}
              />
              {target > 0 ? (
                <ReferenceLine
                  y={target}
                  stroke="#D4A373"
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  label={{
                    value: `Target AED ${(target / 1000).toFixed(0)}k`,
                    fontSize: 10,
                    fill: '#D4A373',
                    position: 'insideTopRight',
                    fontWeight: 600,
                  }}
                />
              ) : null}
              <Area
                type="monotone"
                dataKey="projected"
                stroke="#C084FC"
                strokeWidth={2}
                strokeDasharray="6 4"
                fill="url(#projGrad)"
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="pace"
                stroke="hsl(262 20% 78%)"
                strokeWidth={1}
                strokeDasharray="3 3"
                dot={false}
              />
              <Area
                type="monotone"
                dataKey="v"
                stroke="#7C3AED"
                strokeWidth={2.5}
                fill="url(#revGradPremium)"
                connectNulls
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      <p className="mt-3 text-xs text-muted-foreground">
        Forecast AED {projected.toLocaleString()} ({forecastPct}% of target) ·{' '}
        <span className="font-semibold text-emerald-600">{likelihood}% likelihood</span> of hitting goal
      </p>
    </section>
  )
}
